import { TextField } from "@mui/material";
import React, { useContext, useEffect, useRef, useState } from "react";
import { ITkTextFieldProps } from "../../../type/componentsType/TkLabelText.d";
import { convertFullWidthKanaToHalfWidth } from "../TkLabelText/hooks/useTools";
import "./_style.scss";
import { changeAmountFormat } from "../../../utils/useTools";
import TkPropertyMessageGet from "../../../utils/TkPropertyMessageGet";
import { TkFormContext } from "../../../context/tkFormContext";

/**
 * テキストボックス部品
 * TkLabelTextコンポーネントは、ラベルとテキストフィールドを表示するために使用されます。
 * @param {any} wantToSetValue デフォルトの入力値
 * @param {label} label ラベル
 * @param {React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>} onChange 入力値が変更されたときのイベント
 * @param {string} size テキストボックスのサイズ
 * @param {boolean} onlyNumber 数字のみを許容する
 * @param {boolean} convertToHalfwidth 半角カナに自動変換する
 * @param {boolean} onlyFullWidth 全角のみを許容する
 * @param {boolean} isNumberFormat 数字を3桁毎にフォーマットする
 * @param {any} inputProps
 * @return TkLabelText テキストボックス部品
 */
const TkLabelTextForPassWord = React.forwardRef<
  HTMLInputElement,
  ITkTextFieldProps
>((props, ref) => {
  /**
   * ラベルとonChangeプロパティを取得し、stateを初期化します。
   */
  const {
    value,
    wantToSetValue, // デフォルトの入力値
    label, // ラベル
    onChange, // 入力値が変更されたときのイベント
    size = "small", // テキストボックスのサイズ
    onlyNumber = false, // 数字のみを許容する
    onlyFullWidth = false, // 全角のみを許容する
    convertToHalfwidth = false, // 半角カナに自動変換する
    inputProps, // その他のinput要素のプロパティ
    isNumberFormat = false, // 数字を3桁毎にフォーマットする
    error, // エラーの有無
    helperText, // ヘルパーテキスト
    defaultValue, // デフォルトの入力値
    onGetInputValue, // 入力値を取得する\
    useDefault, // デフォルトの入力値を使用する
    useNumberValue, // 数値の入力値を使用する
    ...rest
  } = props;
  const [state, setState] = useState(""); // 入力値のstate
  const [errorInInput, setErrorInInput] = useState(""); // 入力エラーのstate
  const { formContext, setFormContext } = useContext(TkFormContext); // フォームコンテキストの取得

  const TkLabelTest = useRef(null); // ラベルのref

  // テキストフィールドの幅を自動調整する
  useEffect(() => {
    const elem = TkLabelTest.current as any;
    const observer = new ResizeObserver(() => {});
    if (elem) {
      observer.observe(elem);
    }
    return () => {
      if (elem) {
        observer.unobserve(elem);
      }
    };
  }, []);

  /**
   * 数字をフォーマットする関数
   * @param {any} setValue - フォーマットする値
   * @param {boolean} deFormat - フォーマットを解除するかどうか
   */
  const formatNumber = (setValue: any, deFormat: boolean = false) => {
    const noCommaAmount = setValue?.toString().replace(/,/g, "");

    if (deFormat) {
      setState(noCommaAmount);
      return;
    }

    if (onlyNumber && isNaN(Number(noCommaAmount))) {
      return;
    }
    const formatValue = changeAmountFormat(noCommaAmount);
    setState(formatValue);
  };

  useEffect(() => {
    if (wantToSetValue === state) {
      return;
    }
    setState(wantToSetValue ?? "");

    if (wantToSetValue !== null && wantToSetValue !== undefined) {
      if (isNumberFormat && wantToSetValue !== "") {
        formatNumber(wantToSetValue);
      }
      const parsedValue = +wantToSetValue;
      const value = useNumberValue
        ? isNaN(parsedValue)
          ? wantToSetValue
          : parsedValue
        : wantToSetValue;

      const defaultValue = {
        target: {
          value,
        },
      } as React.ChangeEvent<HTMLInputElement>;

      onChange && onChange(defaultValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantToSetValue]);

  /**
   * テキストフィールドの変更を処理し、onChangeプロパティを呼び出します。
   * @param event - React.ChangeEvent<HTMLInputElement>型のイベントオブジェクト
   */
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newEvent = {
      target: {
        value: event.target.value,
      },
    } as React.ChangeEvent<HTMLInputElement>;

    // 数字のみの場合
    if (onlyNumber && event.target.value !== "") {
      const value = Number(event.target.value);

      if (!isNaN(value)) {
        const newEvent = {
          target: {
            value: useNumberValue ? value : event.target.value,
          },
        } as any;

        setState(useNumberValue ? `${value}` : event.target.value);
        onChange && onChange(newEvent);
        onGetInputValue && onGetInputValue(newEvent);
      }
      return;
    }

    //全角のみの場合
    if (onlyFullWidth) {
      const originalValue = event.target.value;
      const value = originalValue
        .split("")
        // eslint-disable-next-line no-control-regex
        .filter((char) =>
          // eslint-disable-next-line no-control-regex
          /^([^\x00-\x08\x0B\x0C\x0E-\x1F\x7F -~｡-ﾟ])*$/.test(char)
        )
        .join("");

      if (originalValue && value !== originalValue) {
        setFormContext({
          preventSubmit: {
            ...formContext?.preventSubmit,
            labelText: true,
          },
        });
        setErrorInInput(TkPropertyMessageGet("TKMSG_SCR0061E"));
      } else {
        if (formContext?.preventSubmit?.labelText)
          setFormContext({
            preventSubmit: {
              ...formContext?.preventSubmit,
              labelText: false,
            },
          });
        setErrorInInput("");
      }

      const newEvent = {
        target: {
          value: originalValue,
        },
      } as any;

      setState(originalValue);
      onChange && onChange(newEvent);
      return;
    }

    setState(event.target.value);
    onChange && onChange(newEvent);
    onGetInputValue && onGetInputValue(event.target.value);
  };

  /**
   * テキストフィールドのフォーカスが外れたときの処理
   * @param event - React.FocusEvent<HTMLInputElement>型のイベントオブジェクト
   */
  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    // 半角に変換する場合
    if (convertToHalfwidth) {
      const value = convertFullWidthKanaToHalfWidth(event.target.value);
      const newEvent = {
        target: {
          value: value,
        },
      } as any;

      setState(value);
      onChange && onChange(newEvent);
      rest.onBlur && rest.onBlur(newEvent);
      return;
    }

    // 3桁毎にフォーマットの場合
    if (isNumberFormat && event.target.value !== "") {
      formatNumber(event.target.value);
      rest.onBlur && rest.onBlur(event);
      return;
    }

    rest.onBlur && rest.onBlur(event);
  };

  /**
   * テキストフィールドにフォーカスが当たったときの処理
   * @param event - React.FocusEvent<HTMLInputElement>型のイベントオブジェクト
   */
  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    // 3桁毎にフォーマットの場合
    if (isNumberFormat && event.target.value !== "") {
      formatNumber(event.target.value, true);
      rest.onFocus && rest.onFocus(event);
      return;
    }
    rest.onFocus && rest.onFocus(event);
  };

  /**
   * ラベルとテキストフィールドを表示します。
   */
  return (
    <TextField
      ref={TkLabelTest}
      label={label}
      size={size}
      onChange={handleChange}
      {...rest}
      error={error || errorInInput !== ""}
      helperText={helperText || errorInInput}
      onBlur={handleBlur}
      onFocus={handleFocus}
      inputProps={{ ...inputProps, "data-testid": "tkTextField" }}
      autoComplete="new-password" // TkLabelTextと異なるところ
      {...(defaultValue || useDefault ? { defaultValue } : { value: state })}
    />
  );
});

export default TkLabelTextForPassWord;
